/**
 * LayoutPresetPreview Component
 *
 * Data-driven mini-diagram — renders a visual preview of a layout preset
 * from its split tree definition. Walks the splitTree recursively:
 * split nodes become flex containers, slot nodes become colored rectangles.
 *
 * Decorative only (aria-hidden) — label text provides the accessible name.
 * Scales to all current and future presets automatically.
 *
 * @see types/shared/Layout.ts for SplitNodeDefinition
 * @see constants/layout.ts for preset definitions
 */

import React from 'react';

import { LAYOUT_PRESETS } from '@/constants/layout';

import type { LayoutPresetId, SplitNodeDefinition } from '@/types/shared';

// ===== TYPES =====

export interface LayoutPresetPreviewProps {
  presetId: LayoutPresetId;
  className?: string;
}

// ===== HELPERS =====

function renderNode(node: SplitNodeDefinition, depth: number): React.ReactNode {
  if (node.type === 'slot') {
    return <div className="flex-1 rounded-[1px] bg-default-400" />;
  }

  const direction = node.orientation === 'horizontal' ? 'flex-row' : 'flex-col';
  const gap = depth === 0 ? 'gap-[2px]' : 'gap-[1px]';

  return (
    <div className={`flex ${direction} flex-1 ${gap}`}>
      {renderNode(node.first, depth + 1)}
      {renderNode(node.second, depth + 1)}
    </div>
  );
}

// ===== COMPONENT =====

export const LayoutPresetPreview: React.FC<LayoutPresetPreviewProps> = React.memo(({
  presetId,
  className,
}) => {
  const { splitTree } = LAYOUT_PRESETS[presetId];

  return (
    <div
      aria-hidden="true"
      className={`flex w-6 h-5 overflow-hidden rounded-sm shrink-0 ${className ?? ''}`}
    >
      {renderNode(splitTree, 0)}
    </div>
  );
});

LayoutPresetPreview.displayName = 'LayoutPresetPreview';
